/**
 * Phase 4 — Tool Access API client.
 *
 * Thin fetch wrapper matching the existing codebase convention (see
 * `src/lib/api/control-center/control-center-api.ts` /
 * `src/lib/api/identity/identity-api.ts`). RTK Query is intentionally NOT used
 * here — kept consistent with the other Phase 1+ clients until a later phase
 * unifies everything.
 *
 * Auth header: `Authorization: Bearer ${state.auth.agency.accessToken}`.
 * Per CLAUDE.md, this token lives in Redux ONLY and is never persisted.
 *
 * Base URL: `CENTRAL_API_BASE_URL` from `src/lib/constants/central-api.ts`,
 * which already resolves to `${env-base}/api/v1/` across DEV/STAGING/LIVE — so
 * the relative paths below intentionally omit the `/api/v1/` prefix.
 *
 * Backend contract (Phase 3 — built in parallel; this client targets it exactly):
 *   GET   /api/v1/shops/{slug}/tool-access?employee={id}
 *   PUT   /api/v1/shops/{slug}/tool-access
 *   POST  /api/v1/shops/{slug}/tool-access/bulk
 *   POST  /api/v1/shops/{slug}/tool-access/apply-preset
 *   GET   /api/v1/tool-presets
 *   POST  /api/v1/tool-presets
 *   PATCH /api/v1/shops/{slug}/mcp-connections/{id}/tools/{tool}
 *
 * All list/object responses may arrive either bare or `{ data: ... }`-wrapped;
 * `unwrap()` / the array guards below normalise both shapes defensively.
 */
import { CENTRAL_API_BASE_URL } from '../../constants/central-api';
import store from '../../../store/store';

// ---------- shared types ----------

export type ToolClassification = 'read' | 'write';
export type ConnectionPolicy = 'allow' | 'require_approval' | 'block';

/**
 * A single tool policy row for the active brand, joined with the selected
 * employee's effective enablement. One row per (mcp_tool_policy, employee).
 */
export interface ToolAccessRow {
  mcp_tool_policy_id: number | string;
  connection_id: number | string;
  connection_name: string;
  platform_key: string | null;
  tool_name: string;
  exposed_name: string;
  ai_description: string;
  classification: ToolClassification;
  connection_policy: ConnectionPolicy;
  /** The employee's effective enablement for this tool. */
  employee_enabled: boolean;
  /**
   * `false` when `employee_enabled` is inherited from the connection/preset
   * default (no explicit per-employee override row exists); `true` when an
   * explicit override has been set for this employee.
   */
  is_overridden: boolean;
}

/** An employee selectable in the Tool Access grid. */
export interface ToolAccessEmployee {
  id: string;
  name: string;
  role: string;
}

/** Response payload of GET /shops/{slug}/tool-access. */
export interface ToolAccessResponse {
  tools: ToolAccessRow[];
  employees: ToolAccessEmployee[];
}

/** Body item for PUT /shops/{slug}/tool-access and the bulk endpoint. */
export interface ToolAccessOverride {
  mcp_tool_policy_id: number | string;
  agency_employee_id: string;
  enabled: boolean;
}

/** A reusable preset (Read-only / No writes / Full + any custom). */
export interface ToolPreset {
  key: string;
  name: string;
  /**
   * Opaque rule descriptor as authored on the backend. Kept as `unknown` so
   * the UI never depends on its internal shape — presets are applied by `key`.
   */
  rule: unknown;
}

/** Body for POST /tool-presets (create a custom preset). */
export interface CreateToolPresetRequest {
  name: string;
  rule: unknown;
}

/** Body for PATCH …/tools/{tool} — rename/alias + description edit. */
export interface UpdateToolMetaRequest {
  exposed_name?: string;
  ai_description?: string;
}

/** Shape returned by the tool meta PATCH (subset that the grid consumes). */
export interface UpdatedToolMeta {
  tool_name: string;
  exposed_name?: string;
  ai_description?: string;
}

// ---------- internal helpers ----------

function getAuthHeader(token?: string): Record<string, string> {
  const t = token ?? store.getState().auth.agency.accessToken;
  const headers: Record<string, string> = {
    Accept: 'application/json',
  };
  if (t) {
    headers['Authorization'] = `Bearer ${t}`;
  }
  return headers;
}

async function parseJson<T>(response: Response): Promise<T> {
  const text = await response.text();
  if (!text) {
    return {} as T;
  }
  try {
    return JSON.parse(text) as T;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[tool-access-api] failed to parse JSON', err, text);
    throw new Error('Invalid response from server.');
  }
}

async function request<T>(
  path: string,
  init: RequestInit = {},
  tokenOverride?: string
): Promise<T> {
  const headers: Record<string, string> = {
    ...getAuthHeader(tokenOverride),
    ...((init.headers as Record<string, string>) || {}),
  };

  if (init.body && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }

  const response = await fetch(`${CENTRAL_API_BASE_URL}${path}`, {
    ...init,
    headers,
  });

  if (!response.ok) {
    let detail = '';
    let validation: Record<string, string[]> | undefined;
    try {
      const body = await parseJson<{
        message?: string;
        error?: string;
        errors?: Record<string, string[]>;
      }>(response);
      detail = body?.message || body?.error || '';
      validation = body?.errors;
    } catch {
      // swallow — fall back to status text
    }
    const err = new Error(
      detail || `Request failed (${response.status} ${response.statusText})`
    ) as Error & {
      status?: number;
      validation?: Record<string, string[]>;
    };
    err.status = response.status;
    if (validation) err.validation = validation;
    throw err;
  }

  return parseJson<T>(response);
}

/** Unwrap a `{ data }` envelope when present; pass through a bare payload. */
function unwrap<T>(raw: T | { data?: T }): T {
  if (
    raw &&
    typeof raw === 'object' &&
    'data' in (raw as Record<string, unknown>) &&
    (raw as { data?: T }).data !== undefined
  ) {
    return (raw as { data: T }).data;
  }
  return raw as T;
}

function shopBase(slug: string): string {
  return `shops/${encodeURIComponent(slug)}/tool-access`;
}

// ---------- public API ----------

/**
 * GET /api/v1/shops/{slug}/tool-access?employee={id}
 *
 * Returns every tool policy on the brand joined with `employeeId`'s effective
 * enablement, plus the list of employees that can be selected. The `employee`
 * query param is optional — when omitted the backend may default to the first
 * employee; the UI always passes it once an employee is selected.
 *
 * The envelope is normalised so both `{ tools, employees }` and a
 * `{ data: { tools, employees } }` wrapper resolve to the same shape.
 */
export async function getToolAccess(
  slug: string,
  employeeId?: string,
  token?: string
): Promise<ToolAccessResponse> {
  const qs = employeeId
    ? `?employee=${encodeURIComponent(employeeId)}`
    : '';
  const raw = await request<
    ToolAccessResponse | { data?: ToolAccessResponse }
  >(`${shopBase(slug)}${qs}`, { method: 'GET' }, token);
  const data = unwrap<ToolAccessResponse>(raw) ?? ({} as ToolAccessResponse);
  return {
    tools: Array.isArray(data.tools) ? data.tools : [],
    employees: Array.isArray(data.employees) ? data.employees : [],
  };
}

/**
 * PUT /api/v1/shops/{slug}/tool-access
 *
 * Set a single (tool, employee) override. `enabled` is the desired effective
 * state for the employee on that tool.
 */
export async function setToolAccess(
  slug: string,
  override: ToolAccessOverride,
  token?: string
): Promise<void> {
  await request<unknown>(
    shopBase(slug),
    {
      method: 'PUT',
      body: JSON.stringify(override),
    },
    token
  );
}

/**
 * POST /api/v1/shops/{slug}/tool-access/bulk
 *
 * Apply many (tool, employee) overrides in one request — used by the
 * multi-select enable/disable bulk action.
 */
export async function bulkSetToolAccess(
  slug: string,
  overrides: ToolAccessOverride[],
  token?: string
): Promise<void> {
  await request<unknown>(
    `${shopBase(slug)}/bulk`,
    {
      method: 'POST',
      body: JSON.stringify({ overrides }),
    },
    token
  );
}

/**
 * POST /api/v1/shops/{slug}/tool-access/apply-preset
 *
 * Apply a named preset to an employee — replaces their per-tool overrides with
 * the preset's rule for the brand's current tool set.
 */
export async function applyToolPreset(
  slug: string,
  presetKey: string,
  employeeId: string,
  token?: string
): Promise<void> {
  await request<unknown>(
    `${shopBase(slug)}/apply-preset`,
    {
      method: 'POST',
      body: JSON.stringify({
        preset_key: presetKey,
        agency_employee_id: employeeId,
      }),
    },
    token
  );
}

/** GET /api/v1/tool-presets — the reusable presets (built-in + custom). */
export async function listToolPresets(token?: string): Promise<ToolPreset[]> {
  const raw = await request<ToolPreset[] | { data?: ToolPreset[] }>(
    'tool-presets',
    { method: 'GET' },
    token
  );
  if (Array.isArray(raw)) return raw;
  return unwrap<ToolPreset[]>(raw) ?? [];
}

/** POST /api/v1/tool-presets — author a new custom preset. */
export async function createToolPreset(
  body: CreateToolPresetRequest,
  token?: string
): Promise<ToolPreset> {
  const raw = await request<ToolPreset | { data?: ToolPreset }>(
    'tool-presets',
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
    token
  );
  return unwrap<ToolPreset>(raw);
}

/**
 * PATCH /api/v1/shops/{slug}/mcp-connections/{id}/tools/{tool}
 *
 * Rename/alias a tool (`exposed_name`) and/or edit its `ai_description`.
 * The server validates `exposed_name` against `^[a-z0-9_-]{1,128}$` and returns
 * 422 (with `errors`) on failure — surfaced to the caller via `err.validation`.
 */
export async function updateToolMeta(
  slug: string,
  connectionId: number | string,
  toolName: string,
  body: UpdateToolMetaRequest,
  token?: string
): Promise<UpdatedToolMeta> {
  const raw = await request<UpdatedToolMeta | { data?: UpdatedToolMeta }>(
    `shops/${encodeURIComponent(slug)}/mcp-connections/${encodeURIComponent(
      String(connectionId)
    )}/tools/${encodeURIComponent(toolName)}`,
    {
      method: 'PATCH',
      body: JSON.stringify(body),
    },
    token
  );
  return unwrap<UpdatedToolMeta>(raw);
}
